import { baseApi } from "./baseApi";

interface CountryMeta {
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
}

interface CountriesResponse {
  data: { id: string; name: string; flag: string }[];
  meta: CountryMeta;
}

interface CountriesQueryArgs {
  page?: number;
  per_page?: number;
}

interface StateItem {
  id: string;
  name: string;
}

export const settingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    getCompanySettings: builder.query<any, void>({
      query: () => ({ url: `/settings`, method: "GET" }),
      providesTags: ["companies"],
    }),

    updateCompanySettings: builder.mutation<any, { companyId: string; data: Record<string, any> }>({
      query: ({ companyId, data }) => {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value === null || value === undefined) return;
          if (Array.isArray(value)) {
            // A plain FormData.append(key, someArray) silently stringifies
            // it to "a,b,c" — Laravel then sees a string, not an array,
            // and the `array` validation rule fails. Laravel expects
            // repeated key[] entries to parse it as an array.
            value.forEach((item) => formData.append(`${key}[]`, item as string | Blob));
          } else {
            formData.append(key, value as string | Blob);
          }
        });
        return {
          url: `/settings/companies/${companyId}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["companies"],
    }),

    getCompanyNotificationPreferences: builder.query<any, { companyId?: string }>({
      query: ({ companyId }) => ({
        url: `/settings/notification-preferences/${companyId}`,
        method: "GET",
      }),
      transformResponse: (response: any) => response?.data?.data ?? {},
      providesTags: ["companyNotifications"],
    }),

    updateCompanyNotificationPreferences: builder.mutation<any, { companyId: string; data: { notify_email: boolean; notify_inapp: boolean } }>({
      query: ({ companyId, data }) => ({
        url: `/settings/notification-preferences/${companyId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["companyNotifications"],
    }),

    getStaffNotifications: builder.query<any[], { staffMemberId: string }>({
      query: ({ staffMemberId }) => ({
        url: `/settings/staff/${staffMemberId}/notifications`,
        method: "GET",
      }),
      transformResponse: (response: any) => response?.data ?? [],
      providesTags: ["staffNotifications"],
    }),

    deleteStaffNotification: builder.mutation<any, { staffMemberId: string; notificationId: string }>({
      query: ({ staffMemberId, notificationId }) => ({
        url: `/settings/${staffMemberId}/notifications/${notificationId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["staffNotifications"],
    }),

    markStaffNotificationRead: builder.mutation<any, { staffMemberId: string; notificationId: string }>({
      query: ({ staffMemberId, notificationId }) => ({
        url: `/settings/${staffMemberId}/notifications/${notificationId}/read`,
        method: "PUT",
      }),
      invalidatesTags: ["staffNotifications"],
    }),

    getStaffNotificationPreferences: builder.query<any[], { staffMemberId: string }>({
      query: ({ staffMemberId }) => ({
        url: `/settings/staff/${staffMemberId}/notifications`,
        method: "GET",
      }),
      transformResponse: (response: any) => response?.data ?? [],
      providesTags: ["staffNotifications"],
    }),

    getCompanyStaff: builder.query<any, { companyId: string }>({
      query: ({ companyId }) => ({
        url: `/employees/staff-members/by-company?company_id=${companyId}`,
        method: "GET",
      }),
      providesTags: ["staff"],
    }),

    getCountries: builder.query<CountriesResponse, CountriesQueryArgs>({
      query: ({ page = 1, per_page = 50 }) => ({
        url: `/country/getCountry`,
        method: "GET",
        params: { page, per_page },
      }),
      transformResponse: (response: any): CountriesResponse => ({
        data: response?.data?.data ?? [],
        meta: {
          current_page: response?.data?.current_page ?? 1,
          last_page: response?.data?.last_page ?? 1,
          total: response?.data?.total ?? 0,
          per_page: response?.data?.per_page ?? 50,
        },
      }),
      serializeQueryArgs: () => "countries-all",
      merge: (
        currentCache: CountriesResponse,
        newItems: CountriesResponse,
        { arg }: { arg: CountriesQueryArgs }
      ) => {
        if (arg.page === 1) {
          currentCache.data = newItems.data;
        } else {
          const existingIds = new Set(currentCache.data.map((c) => c.id));
          const fresh = newItems.data.filter((c) => !existingIds.has(c.id));
          currentCache.data.push(...fresh);
        }
        currentCache.meta = newItems.meta;
      },
      forceRefetch: ({
        currentArg,
        previousArg,
      }: {
        currentArg?: CountriesQueryArgs;
        previousArg?: CountriesQueryArgs;
      }) => {
        return currentArg?.page !== previousArg?.page;
      },
      providesTags: ["countries"],
    }),

    getStates: builder.query<StateItem[], string>({
      query: (countryId) => ({
        url: `/country/${countryId}/states`,
        method: "GET",
      }),
      transformResponse: (response: any): StateItem[] => response?.data ?? [],
      providesTags: ["states"],
    }),

    getCompanyInfo: builder.query<any, void>({
      query: () => ({ url: `/company/get-company`, method: "GET" }),
      providesTags: ["companyInfo"],
    }),

  }),
});

export const {
  useGetCompanySettingsQuery,
  useUpdateCompanySettingsMutation,
  useGetCompanyNotificationPreferencesQuery,
  useUpdateCompanyNotificationPreferencesMutation,
  useGetStaffNotificationPreferencesQuery,
  useGetStaffNotificationsQuery,
  useDeleteStaffNotificationMutation,
  useMarkStaffNotificationReadMutation,
  useGetCompanyStaffQuery,
  useGetCountriesQuery,
  useGetStatesQuery,
  useGetCompanyInfoQuery,
} = settingsApi;